<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <span class="mb-1 font-medium">שם הכרטיסייה</span>
    <InputText
      v-model="form.name"
      placeholder="למשל: כרטיסיית 10 תספורות"
    />

    <span class="mt-2 mb-1 font-medium">בחירת שירותים</span>
    <select-service
      v-model="selectedServices"
      @services-selected="onServicesSelected"
    />

    <span class="mt-2 mb-1 font-medium">כמות מימושים</span>
    <input-number
      v-model="form.totalUses"
      :min="1"
      :max="100"
      placeholder="כמות המימושים בכרטיסייה"
    />

    <span class="mt-2 mb-1 font-medium">מחיר הכרטיסייה</span>
    <input-number
      v-model="form.price"
      :min="1"
      :maxFractionDigits="0"
      placeholder="מחיר הכרטיסייה"
      mode="currency"
      currency="ILS"
      locale="he-IL"
    />

    <span class="mt-2 mb-1 font-medium">תוקף (חודשים)</span>
    <input-number
      v-model="form.validityMonths"
      :min="1"
      :max="60"
      placeholder="תוקף בחודשים (לא חובה)"
    />

    <settings-toggle
      class="mt-3"
      :status="form.showOnWebsite ?? true"
      :disabled="!hasPayments"
      title="הצג באתר העסק"
      :description="hasPayments ? 'כשכבוי, הכרטיסייה זמינה לשיוך ידני בלבד ולא מוצגת באתר לרכישה' : 'לא ניתן להציג באתר — לא חובר אמצעי תשלום בעסק'"
      @value-changed="(val) => form.showOnWebsite = val"
    />

    <drawer-footer
      class="mt-auto"
      :approveDisable="!canSubmit"
      :approveLoading="loading"
      @approve-click="submit"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { IBundlePackTemplate, IService } from '@torim-app/types';
import SelectService from '@app/components/admin/components/SelectService.vue';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { useToast } from 'vue-toastification';
import { useAdminStore } from '@app/stores';

const toast = useToast();
const adminStore = useAdminStore();
const emit = defineEmits(['close', 'created']);

const form = reactive<Partial<IBundlePackTemplate>>({
  name: '',
  serviceIds: [],
  totalUses: undefined,
  price: undefined,
  validityMonths: undefined,
  active: true,
  showOnWebsite: true
});

// Without a connected payment provider a bundle can't be bought online, so it can never be
// shown on the site — force it off and lock the toggle.
const hasPayments = computed(() => !!adminStore.getBusinessSiteData?.hasPayments);
if (!hasPayments.value) form.showOnWebsite = false;

const selectedServices = ref<IService[]>([]);
const loading = ref(false);

const onServicesSelected = (services: IService[]) => {
  selectedServices.value = services;
  form.serviceIds = services.map(s => s.id);
};

const canSubmit = computed(() =>
  !!form.name &&
  (form.serviceIds?.length ?? 0) > 0 &&
  !!form.totalUses &&
  !!form.price
);

const submit = async () => {
  if (!canSubmit.value) return;
  loading.value = true;
  try {
    const created = await BundlePacksApi.createTemplate(form);
    toast.success('כרטיסייה נוצרה');
    emit('created', created);
    emit('close');
  } catch (e) {
    console.error(e);
    toast.error('שגיאה ביצירת הכרטיסייה');
  } finally {
    loading.value = false;
  }
};
</script>
